import { portalPaths } from '@provision/portal-routes';
import {
  type QuickActionPage,
  type QuickActionPageGroup,
  QuickActionPageGroupId,
  type QuickActionPagePane,
  type QuickActionPathPane,
} from 'src/components/QuickActions/quick-action-pages.types';
import type {
  CustomView,
  CustomViewEntity,
  CustomViewNavItem,
} from 'src/graphql/entities/custom-view';
import { CUSTOM_VIEW_ENTITY_LABELS } from 'src/pages/CustomViews/constants';
import { NAV_TABS } from 'src/utils/navigation/nav-tabs';
import { type NavigationDestination } from 'src/utils/navigation/navigation.types';
import { type NavTabVisibility } from 'src/utils/navigation/use-nav-tab-visibility';

type PageSource = Pick<NavigationDestination, 'id' | 'title' | 'iconData' | 'path'>;

type FavoriteView = PageSource & Pick<CustomViewNavItem, 'customViewId' | 'entityName'>;

type ViewRecord = Pick<
  CustomView,
  'customViewId' | 'name' | 'icon' | 'entityName' | 'isFavorite'
>;

interface BuildQuickActionPageGroupsInput {
  tabVisibility: NavTabVisibility;
  workspaceItems: PageSource[];
  favoriteViews: FavoriteView[];
  customViews: ViewRecord[];
  // record types the Views page lets this user open, in its tab order
  viewEntities: CustomViewEntity[];
  settingsGroups: { items: PageSource[] }[];
}

const GROUP_TITLES: Record<QuickActionPageGroupId, string> = {
  [QuickActionPageGroupId.Workspace]: 'Workspace',
  [QuickActionPageGroupId.Views]: 'Views',
  [QuickActionPageGroupId.Settings]: 'Settings',
};

const GROUP_NAV_PANES: Record<QuickActionPageGroupId, QuickActionPathPane> = {
  [QuickActionPageGroupId.Workspace]: 'home',
  [QuickActionPageGroupId.Views]: 'home',
  [QuickActionPageGroupId.Settings]: 'settings',
};

const NAV_TAB_PAGES: {
  pane: QuickActionPagePane;
  isVisible: (visibility: NavTabVisibility) => boolean;
}[] = [
  { pane: 'inbox', isVisible: (visibility) => visibility.showInboxTab },
  { pane: 'search', isVisible: (visibility) => visibility.showSearchTab },
  { pane: 'sidekick', isVisible: (visibility) => visibility.showSidekickTab },
];

const toTabPage = (pane: QuickActionPagePane): QuickActionPage => ({
  id: `page-tab-${pane}`,
  title: NAV_TABS[pane].label,
  iconData: { name: NAV_TABS[pane].icon, library: 'lucide' },
  target: { kind: 'pane', pane },
});

const toPathPage =
  (groupId: QuickActionPageGroupId) =>
  (item: PageSource): QuickActionPage => ({
    id: `page-${groupId}-${item.id}`,
    title: item.title,
    iconData: item.iconData,
    target: { kind: 'path', path: item.path, navPane: GROUP_NAV_PANES[groupId] },
  });

const toViewPage = (view: FavoriteView): QuickActionPage => ({
  ...toPathPage(QuickActionPageGroupId.Views)({ ...view, id: view.customViewId }),
  recordType: CUSTOM_VIEW_ENTITY_LABELS[view.entityName],
});

const getOtherViewPages = (
  customViews: ViewRecord[],
  viewEntities: CustomViewEntity[],
): QuickActionPage[] =>
  customViews
    .filter((view) => !view.isFavorite && viewEntities.includes(view.entityName))
    .sort(
      (a, b) =>
        viewEntities.indexOf(a.entityName) - viewEntities.indexOf(b.entityName) ||
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    )
    .map((view) =>
      toViewPage({
        id: view.customViewId,
        customViewId: view.customViewId,
        entityName: view.entityName,
        title: view.name,
        iconData: { name: view.icon, library: 'lucide' },
        path: portalPaths.entity.selected(view.entityName, view.customViewId),
      }),
    );

export const buildQuickActionPageGroups = ({
  tabVisibility,
  workspaceItems,
  favoriteViews,
  customViews,
  viewEntities,
  settingsGroups,
}: BuildQuickActionPageGroupsInput): QuickActionPageGroup[] => {
  const pagesByGroup: [QuickActionPageGroupId, QuickActionPage[]][] = [
    [
      QuickActionPageGroupId.Workspace,
      [
        ...NAV_TAB_PAGES.filter(({ isVisible }) => isVisible(tabVisibility)).map(({ pane }) =>
          toTabPage(pane),
        ),
        ...workspaceItems.map(toPathPage(QuickActionPageGroupId.Workspace)),
      ],
    ],
    [
      QuickActionPageGroupId.Views,
      [...favoriteViews.map(toViewPage), ...getOtherViewPages(customViews, viewEntities)],
    ],
    [
      QuickActionPageGroupId.Settings,
      settingsGroups
        .flatMap((group) => group.items)
        .map(toPathPage(QuickActionPageGroupId.Settings)),
    ],
  ];

  return pagesByGroup
    .filter(([, pages]) => pages.length)
    .map(([id, pages]) => ({ id, title: GROUP_TITLES[id], pages }));
};
